import { Directive, ElementRef, inject, input, NgZone, OnChanges, output } from '@angular/core';
// import {initializePhoto, Photo} from "../types/photo";
import Media from '../../../angularJs/src/app/services/media.service';
import { ENVIRONMENT, PIMSEnvironment } from 'environments';
import { dateToString, formatDisplayDate } from '@shared-utils';

@Directive({
  selector: '[file]',
  standalone: true,
})
export class FileDirective implements OnChanges {
  readonly file = input<any>();
  readonly base64Ready = output<string>();
  showTimestamp = input<boolean>();
  private readonly env = inject<PIMSEnvironment>(ENVIRONMENT);

  private elementRef = inject(ElementRef);
  private ngZone = inject(NgZone);
  private Media = inject(Media);

  private render(imagePath: string, orientation = 0, timestamp?: string): void {
    const element = this.elementRef.nativeElement;

    if (element.hasAttribute('setBackground')) {
      element.style.backgroundImage = `url(${imagePath})`;
    } else {
      element.src = imagePath;
    }

    if (timestamp && this.showTimestamp()) {
      this.renderTimestamp(timestamp);
    }

    switch (orientation) {
      case 1:
        // horizontal (normal)
        break;
      case 2:
        // horizontal flip
        break;
      case 3:
        // 180° rotate left
        element.style.transform = 'rotate(180deg)';
        break;
      case 4:
        // vertical flip
        break;
      case 5:
        // vertical flip + 90 rotate right
        break;
      case 6:
        // 90° rotate right
        element.style.transform = 'rotate(90deg)';
        break;
      case 7:
        // horizontal flip + 90 rotate right
        break;
      case 8:
        // 90° rotate left
        element.style.transform = 'rotate(270deg)';
        break;
    }
  }

  private async download(file: any): Promise<void> {
    file = this.getParsedFile(file);
    if (!file || (!file.id && !file.image_uri) || file.deleted) {
      this.render('assets/images/noimage.png', 0);
      this.base64Ready.emit('');
      return;
    }

    this.render('assets/images/loading-3.gif', 0);

    if (file.image_uri) {
      this.render(file.image_uri, 0);
      const fileValue = this.file();
      if (fileValue.file.timestamp) {
        this.render(file.image_uri, 0, fileValue.file.timestamp);
      }
    } else {
      try {
        const { data, headers } = await this.Media.getFile(file.id);
        if (!data.size || data.deleted) {
          this.render('assets/images/noimage.png');
          this.base64Ready.emit('');
        } else {
          const newBlob = new Blob([data], { type: headers('content-type') });
          const base64Data = await this.blobToDataURL(newBlob);
          this.render(base64Data, 0);
          this.base64Ready.emit(base64Data);
        }
      } catch (error) {
        this.render('assets/images/noimage.png');
        this.base64Ready.emit('');
      }
    }
  }

  private getParsedFile(file: any): any {
    if (!file.file_id && !file.file) {
      return null;
    }
    return {
      id: file.file_id ? file.file_id : file.file.id,
      deleted: file.file.deleted,
      image_uri: file.file_id ? file.image_uri : file.file.image_uri,
    };
  }

  private async blobToDataURL(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
      reader.readAsDataURL(blob);
    });
  }

  private renderTimestamp(timestamp: string): void {
    const element = this.elementRef.nativeElement;

    if (!timestamp) {
      const existingTimestamp = element.querySelector('.file-timestamp');
      if (existingTimestamp) {
        existingTimestamp.remove();
      }
      return;
    }

    let timestampEl = element.querySelector('.file-timestamp');
    if (!timestampEl) {
      timestampEl = document.createElement('div');
      const parent = element.parentNode as HTMLElement;
      const parentRect = parent.getBoundingClientRect();

      const bottomPos = parentRect.height / 10;
      timestampEl.className = 'file-timestamp';
      timestampEl.style.cssText = `
      position: absolute;
      bottom: ${bottomPos}px;
      background: rgba(0,0,0,0.7);
      color: white;
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 12px;
      pointer-events: none;
      order: 2;
    `;
      element.after(timestampEl);
    }
    timestampEl.textContent = formatDisplayDate(timestamp, true);
  }

  ngOnChanges(): void {
    if (this.file()) {
      this.ngZone.runOutsideAngular(() => {
        this.download(this.file());
      });
    }
  }
}
